/**
 * One transcription request fetches a single master format (SRT) from the
 * backend; every output format the user can pick — including plain text — is
 * derived from it locally. That's what makes "1 upload = 1 credit = all
 * formats" possible without extra STT API calls.
 */

import type { OutputFormat } from '../types/transcription'

export interface Cue {
  start: number // seconds
  end: number // seconds
  text: string
}

// The frame rate the gateway declares in its own MicroDVD header, and the
// one we write into ours — a headerless file is read at this rate too.
export const MICRODVD_FPS = 30

export const EXPORT_FORMATS: { format: OutputFormat; ext: string; label: string }[] = [
  { format: 'text', ext: 'txt', label: 'Plain Text' },
  { format: 'srt', ext: 'srt', label: 'SRT' },
  { format: 'vtt', ext: 'vtt', label: 'VTT' },
  { format: 'ass', ext: 'ass', label: 'ASS' },
  { format: 'ssa', ext: 'ssa', label: 'SSA' },
  { format: 'microdvd', ext: 'sub', label: 'MicroDVD' },
  { format: 'mpl2', ext: 'mpl', label: 'MPL2' },
  { format: 'tmp', ext: 'tmp', label: 'TMP' },
]

/** The name a format is shown under, anywhere a person reads it.
 *
 * Never `format.toUpperCase()`. That shortcut printed "MICRODVD" next to a
 * picker offering "MicroDVD" — twice caught and fixed in one file at a time,
 * and it turned up in a third the round after. The name has exactly one
 * home, and this is the only way to ask for it. Callers that show `text`
 * under their own word (a tab, a picker entry) handle that case themselves;
 * everything else is the format's own name. */
export function formatLabel(format: string): string {
  return EXPORT_FORMATS.find((f) => f.format === format)?.label ?? format.toUpperCase()
}

function timeToSeconds(t: string): number {
  const m = t.trim().match(/^(\d{1,2}):(\d{2}):(\d{2})[,.](\d{1,3})$/)
  if (!m) return NaN
  return (
    parseInt(m[1], 10) * 3600 +
    parseInt(m[2], 10) * 60 +
    parseInt(m[3], 10) +
    parseInt(m[4].padEnd(3, '0'), 10) / 1000
  )
}

/** Parse the master SRT returned by the backend into timed cues. */
export function parseSrtToCues(raw: string): Cue[] {
  const cues: Cue[] = []
  for (const part of raw.trim().split(/\n\s*\n/)) {
    const lines = part.trim().split('\n')
    if (lines.length < 2) continue
    // Index line is optional in the wild — find the timing line
    const timeIdx = lines.findIndex((l) => l.includes('-->'))
    if (timeIdx === -1) continue
    const [startRaw, endRaw] = lines[timeIdx].split('-->')
    const start = timeToSeconds(startRaw)
    const end = timeToSeconds(endRaw)
    if (isNaN(start) || isNaN(end)) continue
    const text = lines.slice(timeIdx + 1).join('\n').trim()
    if (!text) continue
    cues.push({ start, end, text })
  }
  return cues
}

function pad(n: number, len = 2): string {
  return String(n).padStart(len, '0')
}

function splitTime(t: number): { h: number; m: number; s: number; ms: number } {
  const total = Math.max(0, t)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = Math.floor(total % 60)
  const ms = Math.round((total - Math.floor(total)) * 1000)
  return { h, m, s, ms }
}

function srtTime(t: number): string {
  const { h, m, s, ms } = splitTime(t)
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(ms, 3)}`
}

function vttTime(t: number): string {
  const { h, m, s, ms } = splitTime(t)
  return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(ms, 3)}`
}

// ASS/SSA use H:MM:SS.cc (centiseconds)
function assTime(t: number): string {
  const { h, m, s, ms } = splitTime(t)
  return `${h}:${pad(m)}:${pad(s)}.${pad(Math.min(99, Math.round(ms / 10)), 2)}`
}

// TMP is HH:MM:SS:text — two-digit hours like the gateway writes them;
// third-party parsers aren't as forgiving as ours
function tmpTime(t: number): string {
  const { h, m, s } = splitTime(t)
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

function toSrt(cues: Cue[]): string {
  return cues
    .map((c, i) => `${i + 1}\n${srtTime(c.start)} --> ${srtTime(c.end)}\n${c.text}`)
    .join('\n\n') + '\n\n'
}

// The gateway numbers its VTT cues like SRT does. The number is optional in
// the format (and our parser skips it either way), but a file built here has
// to look like the one the server delivers.
function toVtt(cues: Cue[]): string {
  return (
    'WEBVTT\n\n' +
    cues
      .map((c, i) => `${i + 1}\n${vttTime(c.start)} --> ${vttTime(c.end)}\n${c.text}`)
      .join('\n\n') +
    '\n\n'
  )
}

// Headers mirror the gateway's own ASS/SSA output byte for byte (including
// its two credit comments and the PlayRes canvas the backend adds), so a file
// built here after an edit, a translation, a re-cut or from history renders —
// and diffs — exactly like the one the server delivered:
// ScaledBorderAndShadow keeps the outline scaling with the video, PlayRes
// 384×288 (libass's default) stops every renderer guessing its own canvas.
const ASS_HEADER = `[Script Info]
; Script generated by Lingvanex Speech Recognition Service
; https://lingvanex.com/products/
WrapStyle: 0
ScaledBorderAndShadow: yes
Collisions: Normal
PlayResX: 384
PlayResY: 288
ScriptType: v4.00+

[V4+ Styles]
Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding
Style: Default,Arial,20,&H00FFFFFF,&H000000FF,&H00000000,&H00000000,0,0,0,0,100,100,0,0,1,2,2,2,10,10,10,1

[Events]
Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text
`

const SSA_HEADER = `[Script Info]
; Script generated by Lingvanex Speech Recognition Service
; https://lingvanex.com/products/
WrapStyle: 0
ScaledBorderAndShadow: yes
Collisions: Normal
PlayResX: 384
PlayResY: 288
ScriptType: v4.00

[V4 Styles]
Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, TertiaryColour, BackColour, Bold, Italic, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, AlphaLevel, Encoding
Style: Default,Arial,20,16777215,255,0,0,0,0,1,2,2,2,10,10,10,0,1

[Events]
Format: Marked, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text
`

function toAss(cues: Cue[]): string {
  const events = cues
    .map(
      (c) =>
        `Dialogue: 0,${assTime(c.start)},${assTime(c.end)},Default,,0,0,0,,${c.text.replace(/\n/g, '\\N')}`,
    )
    .join('\n')
  return ASS_HEADER + events + '\n'
}

function toSsa(cues: Cue[]): string {
  const events = cues
    .map(
      (c) =>
        `Dialogue: Marked=0,${assTime(c.start)},${assTime(c.end)},Default,,0,0,0,,${c.text.replace(/\n/g, '\\N')}`,
    )
    .join('\n')
  return SSA_HEADER + events + '\n'
}

// MicroDVD stores FRAME numbers, not time: without the {0}{0}fps header a
// player assumes the video's frame rate, and a 5-minute file drifts by up
// to 50 s on 30 fps video. Our parser defaulted to the same 25, which is
// why it never showed inside the app.
function toMicrodvd(cues: Cue[]): string {
  return (
    `{0}{0}${MICRODVD_FPS.toFixed(1)}\n` +
    cues
      .map(
        (c) =>
          `{${Math.round(c.start * MICRODVD_FPS)}}{${Math.round(c.end * MICRODVD_FPS)}}${c.text.replace(/\n/g, '|')}`,
      )
      .join('\n') + '\n'
  )
}

// MPL2 counts tenths of a second, and the gateway truncates rather than
// rounds — matching it keeps both paths on the same frame
function toMpl2(cues: Cue[]): string {
  return (
    cues
      .map(
        (c) =>
          `[${Math.floor(c.start * 10)}][${Math.floor(c.end * 10)}]${c.text.replace(/\n/g, '|')}`,
      )
      .join('\n') + '\n'
  )
}

function toTmp(cues: Cue[]): string {
  return cues.map((c) => `${tmpTime(c.start)}:${c.text.replace(/\n/g, ' ')}`).join('\n') + '\n'
}

// The gateway's plain text is a flowing transcript — one paragraph, blocks
// separated by two spaces. Line breaks here would make the same transcript
// look like a different document depending on which path produced it.
// The gateway separates its reading blocks with two spaces, whatever they end
// with, and reproducing that exactly is the whole point of these serializers.
// Our own subtitle lines are a different thing: one sentence is routinely cut
// across three of them, and gluing those back with two spaces each put double
// gaps in the middle of sentences. So when the cues are lines WE cut, the wide
// gap is kept only where a thought actually ended.
const ENDS_A_THOUGHT = /[.!?…:;。！？]["'”»）)\]]*$/

function toPlainText(cues: Cue[], fromSubtitleLines: boolean): string {
  if (!fromSubtitleLines) return cues.map((c) => c.text).join('  ')
  return cues.reduce((out, cue, i) => {
    if (i === 0) return cue.text
    const gap = ENDS_A_THOUGHT.test(cues[i - 1].text.trim()) ? '  ' : ' '
    return out + gap + cue.text
  }, '')
}

export interface SerializeOptions {
  /** These cues are subtitle lines re-cut by `lib/subtitleSegment.ts`, not
   * the gateway's reading blocks. Only plain text cares: everything else
   * writes one cue per cue either way. */
  fromSubtitleLines?: boolean
}

export function serializeCues(
  format: OutputFormat,
  cues: Cue[],
  options: SerializeOptions = {},
): string {
  switch (format) {
    case 'srt':
      return toSrt(cues)
    case 'vtt':
      return toVtt(cues)
    case 'ass':
      return toAss(cues)
    case 'ssa':
      return toSsa(cues)
    case 'microdvd':
      return toMicrodvd(cues)
    case 'mpl2':
      return toMpl2(cues)
    case 'tmp':
      return toTmp(cues)
    default:
      return toPlainText(cues, !!options.fromSubtitleLines)
  }
}

// ── Parsing timed cues out of any server format ──
// The server may fail to deliver individual formats (upstream hiccups), so
// timing information is recovered from whichever timed format is available.

// "MM:SS.mmm" (VTT without hours)
function shortClockToSeconds(t: string): number {
  const m = t.trim().match(/^(\d{1,2}):(\d{2})[.,](\d{1,3})$/)
  if (!m) return NaN
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + parseInt(m[3].padEnd(3, '0'), 10) / 1000
}

function clockToSeconds(t: string): number {
  const full = timeToSeconds(t)
  return isNaN(full) ? shortClockToSeconds(t) : full
}

function parseVttToCues(raw: string): Cue[] {
  const cues: Cue[] = []
  const content = raw.replace(/^WEBVTT[^\n]*\n?/, '')
  for (const part of content.trim().split(/\n\s*\n/)) {
    const lines = part.trim().split('\n')
    const timeIdx = lines.findIndex((l) => l.includes('-->'))
    if (timeIdx === -1) continue
    const [startRaw, endRaw] = lines[timeIdx].split('-->')
    const start = clockToSeconds(startRaw)
    const end = clockToSeconds((endRaw || '').trim().split(/\s+/)[0] || '')
    if (isNaN(start) || isNaN(end)) continue
    const text = lines.slice(timeIdx + 1).join('\n').trim()
    if (text) cues.push({ start, end, text })
  }
  return cues
}

// Dialogue: Layer/Marked,Start,End,Style,Name,MarginL,MarginR,MarginV,Effect,Text
const ASS_EVENT = /^Dialogue:\s*[^,]*,\s*([^,]+?)\s*,\s*([^,]+?)\s*,(?:[^,]*,){6}(.*)$/

function parseAssToCues(raw: string): Cue[] {
  const cues: Cue[] = []
  for (const line of raw.split('\n')) {
    const m = line.match(ASS_EVENT)
    if (!m) continue
    const start = clockToSeconds(m[1])
    const end = clockToSeconds(m[2])
    if (isNaN(start) || isNaN(end)) continue
    const text = m[3].replace(/\\N/gi, '\n').replace(/\{[^}]*\}/g, '').trim()
    if (text) cues.push({ start, end, text })
  }
  return cues
}

function parseMpl2ToCues(raw: string): Cue[] {
  const cues: Cue[] = []
  for (const line of raw.split('\n')) {
    const m = line.match(/^\[(\d+)\]\[(\d+)\]\s?(.*)$/)
    if (!m) continue
    const text = m[3].replace(/\|/g, '\n').trim()
    if (text) cues.push({ start: parseInt(m[1], 10) / 10, end: parseInt(m[2], 10) / 10, text })
  }
  return cues
}

function parseMicrodvdToCues(raw: string): Cue[] {
  const cues: Cue[] = []
  let fps = MICRODVD_FPS
  for (const line of raw.split('\n')) {
    const m = line.match(/^\{(\d+)\}\{(\d+)\}(.*)$/)
    if (!m) continue
    const startFrame = parseInt(m[1], 10)
    const endFrame = parseInt(m[2], 10)
    const body = m[3].trim()
    // Optional header line declaring the frame rate: {0}{0}30.0
    if (startFrame === 0 && endFrame === 0 && /^\d+(\.\d+)?$/.test(body)) {
      fps = parseFloat(body) || fps
      continue
    }
    const text = body.replace(/\|/g, '\n').trim()
    if (text) cues.push({ start: startFrame / fps, end: endFrame / fps, text })
  }
  return cues
}

function parseTmpToCues(raw: string): Cue[] {
  const stamps: { start: number; text: string }[] = []
  for (const line of raw.split('\n')) {
    const m = line.match(/^(\d{1,2}):(\d{2}):(\d{2})[:,](.*)$/)
    if (!m) continue
    const start = parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 + parseInt(m[3], 10)
    const text = m[4].replace(/\|/g, '\n').trim()
    if (text) stamps.push({ start, text })
  }
  return stamps.map((s, i) => ({
    start: s.start,
    end: stamps[i + 1] ? stamps[i + 1].start : s.start + 3,
    text: s.text,
  }))
}

/** One reader per timed format — the mirror image of serializeCues. Exported
 * because the subtitle converter reads files the user brings in, not only
 * what the gateway returned. */
export const CUE_PARSERS: Record<string, (raw: string) => Cue[]> = {
  srt: parseSrtToCues,
  vtt: parseVttToCues,
  ass: parseAssToCues,
  ssa: parseAssToCues,
  mpl2: parseMpl2ToCues,
  microdvd: parseMicrodvdToCues,
  tmp: parseTmpToCues,
}

// Preference order when several formats are available: SRT is the master the
// serializers were verified against, so it goes first.
const CUE_SOURCES: [string, (raw: string) => Cue[]][] = [
  ['srt', CUE_PARSERS.srt],
  ['vtt', CUE_PARSERS.vtt],
  ['ass', CUE_PARSERS.ass],
  ['ssa', CUE_PARSERS.ssa],
  ['mpl2', CUE_PARSERS.mpl2],
  ['microdvd', CUE_PARSERS.microdvd],
  ['tmp', CUE_PARSERS.tmp],
]

/** Timed cues from the best available server format (srt preferred). */
export function cuesFromFormats(formats: Record<string, string>): Cue[] | null {
  for (const [fmt, parse] of CUE_SOURCES) {
    const raw = formats[fmt]
    if (!raw) continue
    try {
      const cues = parse(raw)
      if (cues.length > 0) return cues
    } catch {
      // malformed — try the next source
    }
  }
  return null
}
